import { createContext, useContext, useState, useEffect, useMemo, useCallback, useSyncExternalStore } from 'react'

const API_BASE = import.meta.env.VITE_API_BASE ?? ''

const LiveDataContext = createContext(null)

/**
 * The live stream's state lives in a small store outside React. Each event replaces
 * only its own slice, so the other slices keep their identity.
 */
export function createLiveStore() {
  let state = {
    energyFlow: null, energyFlowOffline: false,
    battery: null, batteryOffline: false,
    houseBatterySoC: null,
    iobrokerDashboard: null, iobrokerOffline: false, iobrokerUpdatedAt: null,
  }
  const listeners = new Set()
  return {
    get: () => state,
    set(patch) {
      const next = { ...state, ...patch }
      if ('battery' in patch && patch.battery !== state.battery) {
        const b = patch.battery
        next.houseBatterySoC = b ? { soc: b.soc, voltage: b.voltage, temperature: b.temperature } : null
      }
      state = next
      for (const l of listeners) l()
    },
    subscribe(l) { listeners.add(l); return () => listeners.delete(l) },
  }
}

/**
 * The live snapshot, re-rendering only when a field this component actually read has
 * changed: a card that reads `battery` no longer re-renders on every 2 s energy-flow
 * event. Reads are tracked (any access, also later in callbacks, counts); until the
 * component has read anything, every change re-renders it.
 */
export function useLiveData() {
  const store = useContext(LiveDataContext)
  // Which fields this component read, and the snapshot it last rendered with. A plain
  // object held for the component's lifetime: written by the read proxy, read by React
  // when the store changes.
  const [tracker] = useState(() => ({ used: new Set(), last: null }))

  const getSnapshot = useCallback(() => {
    const s = store.get()
    const prev = tracker.last
    if (prev && (prev === s || (tracker.used.size > 0 && [...tracker.used].every(k => prev[k] === s[k])))) return prev
    tracker.last = s
    return s
  }, [store, tracker])

  const snap = useSyncExternalStore(store.subscribe, getSnapshot)
  return useMemo(() => new Proxy(snap, {
    get(target, key) {
      if (typeof key === 'string') tracker.used.add(key)
      return target[key]
    },
  }), [snap, tracker])
}

export function LiveDataProvider({ children }) {
  const [store] = useState(createLiveStore)
  const [refreshNonce, setRefreshNonce] = useState(0)

  // Pull-to-refresh (and any `yokan:refresh`) reconnects the stream. The server
  // re-pushes current energy-flow / battery / dashboard snapshots on connect,
  // so this forces an immediate live re-poll and recovers a dropped stream.
  useEffect(() => {
    const onRefresh = () => setRefreshNonce(n => n + 1)
    document.addEventListener('yokan:refresh', onRefresh)
    return () => document.removeEventListener('yokan:refresh', onRefresh)
  }, [])

  useEffect(() => {
    const es = new EventSource(`${API_BASE}/sse/live`)

    es.addEventListener('energy-flow', e => {
      try { store.set({ energyFlow: JSON.parse(e.data), energyFlowOffline: false }) } catch {}
    })

    es.addEventListener('battery', e => {
      try { store.set({ battery: JSON.parse(e.data), batteryOffline: false }) } catch {}
    })

    es.addEventListener('iobroker-dashboard', e => {
      try {
        store.set({ iobrokerDashboard: JSON.parse(e.data), iobrokerOffline: false, iobrokerUpdatedAt: new Date() })
      } catch {}
    })

    es.onerror = () => {
      const s = store.get()
      if (!s.energyFlowOffline || !s.batteryOffline || !s.iobrokerOffline) {
        store.set({ energyFlowOffline: true, batteryOffline: true, iobrokerOffline: true })
      }
    }

    return () => es.close()
  }, [refreshNonce, store])

  return <LiveDataContext.Provider value={store}>{children}</LiveDataContext.Provider>
}

// For tests: render consumers against a given store.
export const LiveDataStoreProvider = LiveDataContext.Provider
